import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import useSWR, { type Fetcher } from 'swr';
import { z } from 'zod';

import { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import type {
  LAIUseFormReturn,
  LiftAIFormProps,
} from '~/components/uploadDocument/SelectDocumentTypeDialog';
import { isLiftAIDocumentPDFUntagged } from '~/components/uploadDocument/UploadDocumentDialogFlow';
import { associateFileToEntity } from '~/components/utils/upload';
import { certificatesList } from '~/data/generated/endpoints/certificates/certificates';
import { inspectionReportsList } from '~/data/generated/endpoints/inspection-reports/inspection-reports';
import { invoicesList, invoicesRetrieve } from '~/data/generated/endpoints/invoices/invoices';
import { ticketsList, ticketsRetrieve } from '~/data/generated/endpoints/tickets/tickets';
import type { CertificateList, InspectionReportList } from '~/data/generated/model';
import { CertificatesListResponse } from '~/data/generated/zod/certificates/certificates.zod';
import { InspectionReportsListResponse } from '~/data/generated/zod/inspection-reports/inspection-reports.zod';
import { parseApiResponse } from '~/data/parseApiResponse';
import type { API_InvoiceFilterResponse } from '~/data/schemas/invoice';
import { invoiceFormOptionSchema, invoiceSchema } from '~/data/schemas/invoice';
import { type API_TicketFilterResponse, ticketSchema } from '~/data/schemas/ticket';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';

import type { FieldDef } from '../form/utils';
import { createFormInvalidHandler, FieldAndCell } from '../form/utils';

export type SupportingDataOnSubmitData = {
  number: string;
  propertyName: string;
};

const supportingDataIdFields = [
  'invoiceId',
  'ticketId',
  'certificateId',
  'inspectionReportId',
] as const;

const addSupportingDataSchema = z
  .object({
    invoiceId: z.string().optional(),
    ticketId: z.string().nullable(),
    certificateId: z.string().nullable(),
    inspectionReportId: z.string().nullable(),
    isContinued: z.boolean().optional(),
    invoiceKind: z.string().nullish(),
    supportingDataMovingForward: z.boolean().nullish(),
    remainingPages: z.number().nullish(),
    subsequentPagesAsContinued: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.isContinued) {
      return;
    }
    if (supportingDataIdFields.some((field) => !!data[field])) {
      return;
    }
    // Only one of these fields is ever rendered at a time (SupportingDataFields
    // picks the field for the current document type), so the issue is attached
    // to all four paths: whichever one is on screen surfaces the error.
    supportingDataIdFields.forEach((field) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one ID must be provided',
        path: [field],
      });
    });
  });

export type SupportingDataDto = z.infer<typeof addSupportingDataSchema>;

// Carried in form state by SelectDocumentTypeDialog, never rendered: an error
// on one can highlight no field, so it reports as a data problem (AMP-695).
const carrierFields = new Set<string>([
  'isContinued',
  'invoiceKind',
  'supportingDataMovingForward',
  'remainingPages',
  'subsequentPagesAsContinued',
]);

const getDefaultValues = (): DefaultValues<SupportingDataDto> => {
  return {
    invoiceId: '',
    ticketId: null,
    certificateId: '',
    inspectionReportId: '',
  };
};

export const useSupportingDataForm = (): LAIUseFormReturn<SupportingDataDto> => {
  const hookForm = useForm<SupportingDataDto>({
    mode: 'all',
    defaultValues: getDefaultValues(),
    resolver: zodResolver(addSupportingDataSchema),
  });

  return {
    hookForm,
    Form: SupportingDataForm,
    Fields: SupportingDataFields,
  };
};

const SupportingDataForm = ({
  children,
  document,
  onSubmit,
  initialData,
}: LiftAIFormProps<SupportingDataDto, SupportingDataOnSubmitData>) => {
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { handleSubmit, reset, control } = useFormContext<SupportingDataDto>();

  useEffect(() => {
    if (!initialData) {
      return;
    }

    reset({
      ...getDefaultValues(),
      ...(initialData ?? {}),
    });
  }, [initialData, reset]);

  const { supportingDataMovingForward, remainingPages, isContinued, subsequentPagesAsContinued } =
    useWatch({ control });

  if (!document) {
    console.error('No document provided to SupportingDataForm');
    return <></>;
  }

  return (
    <form
      noValidate
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onSubmit={handleSubmit(
        async (data) => {
          try {
            if (isLiftAIDocumentPDFUntagged(document)) {
              if ('ticketId' in data && data.ticketId) {
                if (subsequentPagesAsContinued) {
                  const docs = new Array(document.pageNum - 1 + (remainingPages ?? 0)).fill(0);
                  // We cannot optimize this with Promise.all as upload order is important
                  for (let i = 0; i < docs.length; i++) {
                    await associateFileToEntity(document.attachment.id, data.ticketId, 'ticket', {
                      pageNumber: document.pageNum + i,
                      isPrimary: true,
                      isNextPage: true,
                    });
                  }
                } else {
                  await associateFileToEntity(document.attachment.id, data.ticketId, 'ticket', {
                    pageNumber: document.pageNum,
                    isPrimary: false,
                    isNextPage: true,
                  });
                }
                const { data: ticketData } = await ticketsRetrieve(data.ticketId);
                const ticket = parseApiResponse(ticketSchema, ticketData, 'Ticket');
                onSubmit({
                  number: ticket.number,
                  // A ticket whose Property was deleted (`Ticket.property` is
                  // `SET_NULL`) has a null `property` on read; fall back rather
                  // than crash on the null-property case the strict schema now
                  // surfaces (AMP-593).
                  propertyName: ticket.property?.name ?? 'Unknown Property',
                });
              } else if ('certificateId' in data && data.certificateId) {
                await associateFileToEntity(
                  document.attachment.id,
                  data.certificateId,
                  'certificate',
                  {
                    pageNumber: document.pageNum,
                    isPrimary: false,
                    isNextPage: true,
                  },
                );
                onSubmit({
                  number: data.certificateId.slice(0, 8),
                  propertyName: 'Certificate Continuation',
                });
              } else if ('inspectionReportId' in data && data.inspectionReportId) {
                await associateFileToEntity(
                  document.attachment.id,
                  data.inspectionReportId,
                  'inspection_report',
                  {
                    pageNumber: document.pageNum,
                    isPrimary: false,
                    isNextPage: true,
                  },
                );
                onSubmit({
                  number: data.inspectionReportId.slice(0, 8),
                  propertyName: 'Inspection Report',
                });
              } else if ('invoiceId' in data && data.invoiceId) {
                const invoiceId = data.invoiceId;
                if (subsequentPagesAsContinued) {
                  const docs = new Array(document.pageNum - 1 + (remainingPages ?? 0)).fill(0);
                  // We cannot optimize this with Promise.all as upload order is important
                  for (let i = 0; i < docs.length; i++) {
                    await associateFileToEntity(document.attachment.id, invoiceId, 'invoice', {
                      pageNumber: document.pageNum + i,
                      isPrimary: true,
                      isNextPage: true,
                    });
                  }
                } else if (isContinued) {
                  await associateFileToEntity(document.attachment.id, invoiceId, 'invoice', {
                    pageNumber: document.pageNum,
                    isPrimary: true,
                    isNextPage: true,
                  });
                } else if (supportingDataMovingForward) {
                  const docs = new Array(document.pageNum + (remainingPages ?? 0)).fill(0);
                  // We cannot optimize this with Promise.all as upload order is important
                  for (let i = 0; i < docs.length; i++) {
                    await associateFileToEntity(document.attachment.id, invoiceId, 'invoice', {
                      pageNumber: document.pageNum + i,
                    });
                  }
                } else {
                  await associateFileToEntity(document.attachment.id, invoiceId, 'invoice', {
                    pageNumber: document.pageNum,
                  });
                }

                const { data: invoiceData } = await invoicesRetrieve(invoiceId);
                // Parse through the corrected `invoiceSchema` (not the raw
                // generated `InvoicesRetrieveResponse`, which types
                // consultant/serviceProvider as required non-null objects), and
                // narrow to the two fields this confirmation actually reads.
                // The raw schema would mismatch on every unassigned invoice —
                // now a Sentry report and a relaxed re-parse on a routine
                // payload rather than a throw, but still a false positive in
                // exactly the data the boundary is meant to be measuring.
                const invoice = parseApiResponse(
                  invoiceSchema.pick({ number: true, property: true }),
                  invoiceData,
                  'Invoice',
                );
                onSubmit({
                  number: invoice.number,
                  // `Invoice.property` is `FK(null=True, SET_NULL)`
                  // (models/invoice.py:91-93), so a deleted Property leaves a
                  // null here and the relaxed tier delivers it as null rather
                  // than throwing. Mirrors the ticket branch above.
                  propertyName: invoice.property?.name ?? 'Unknown Property',
                });
              }
            }

            showEntityActionSnackbar(
              {
                name: document.documentType ?? 'Document',
                action: ActionEnum.Create,
                id: undefined,
              },
              {
                variant: 'info',
              },
            );
          } catch (err) {
            console.error('err :>> ', err);
          }
        },
        createFormInvalidHandler<SupportingDataDto>({
          formName: 'SupportingDataForm',
          showSnackbar,
          isHiddenField: (fieldPath) => carrierFields.has(fieldPath),
        }),
      )}
    >
      {children}
    </form>
  );
};

const getForProp = (documentType?: UploadDocumentType) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (documentType) {
    case UploadDocumentType.supportingData:
    case UploadDocumentType.invoiceContinued:
    case UploadDocumentType.proposalContinued:
      return 'invoice';
    case UploadDocumentType.ticketContinued:
      return 'ticket';
    case UploadDocumentType.certificateContinued:
      return 'certificate';
    case UploadDocumentType.inspectionReportContinued:
      return 'inspectionReport';
    default:
      return '';
  }
};

const ticketOrInvoiceCertificateOptionsFetcher: Fetcher<
  | API_InvoiceFilterResponse['results']
  | API_TicketFilterResponse['results']
  | CertificateList[]
  | InspectionReportList[],
  readonly [
    'SupportingDataForm.Options',
    forProp: 'ticket' | 'invoice' | 'certificate' | 'inspectionReport',
  ]
> = async ([_, forProp]) => {
  // These list endpoints return the same wire shapes the legacy filter methods
  // did; the generated row types differ only nominally, so cast at the boundary
  // to the hand-written filter-result types this fetcher exposes (AMP-507).
  switch (forProp) {
    case 'ticket': {
      const { data } = await ticketsList({ ordering: 'number' });
      const results = Array.isArray(data)
        ? parseApiResponse(ticketSchema.array(), data, 'Ticket')
        : parseApiResponse(ticketSchema.array(), data?.results, 'Ticket');
      return results;
    }
    case 'invoice': {
      const { data } = await invoicesList({ ordering: 'number', variant: 'form' });
      // `InvoicePaginator` (invoices/viewset.py) inherits
      // `default_limit = api_settings.PAGE_SIZE`, and REST_FRAMEWORK
      // (settings/base.py) sets no PAGE_SIZE — so it is None, nothing here
      // sends `limit`, and the LIVE response is a bare array. The array branch
      // used to return `data` unparsed, which meant the real payload skipped
      // the boundary entirely and the envelope branch this was measuring never
      // executes. Normalize the shape first, then parse once, so either shape
      // is observed (mirrors TicketForm.tsx:48-56).
      const results = Array.isArray(data) ? data : data?.results;
      // AMP-620: `invoiceFormOptionSchema` is the lean `variant=form` row
      // (id/number/property/serviceProvider only); `API_InvoiceFilterResponse`
      // always declares the richer `InvoiceWithTickets` shape. Same
      // lean-vs-rich-variant erasure as `fetchInvoicesFilter.ts`'s documented
      // boundary cast — this caller only reads the lean fields, so it's safe
      // in practice, but not provable from the types without a
      // variant-conditional return type (out of scope here).
      // eslint-disable-next-line local/no-parse-api-response-cast
      return parseApiResponse(
        invoiceFormOptionSchema.array(),
        results,
        'Invoice',
      ) as API_InvoiceFilterResponse['results'];
    }
    // AMP-619: both of these now parse against a base that describes what the
    // server actually sends. `CertificateViewSet.list` /
    // `InspectionReportViewSet.list` build their own `{metadata, results}`
    // envelope, and each is declared with `responses=` now, so the generated
    // type no longer infers the stock paginated envelope with a required `count`
    // that no caller here ever receives (nothing sends `limit`, so the paginator
    // never engages). The unreachable `Array.isArray` guard that used to bridge
    // the two shapes — and the `any` it smuggled in to make a cast to the
    // parallel hand-written certificate / inspection-report types legal (those
    // types are gone as of AMP-710) — are both gone with it: these return the
    // generated row types directly, so one declaration of the wire shape is
    // left, not two.
    case 'certificate': {
      const { data } = await certificatesList({ ordering: ['expiration_date'] });
      return parseApiResponse(CertificatesListResponse, data, 'Certificate').results;
    }
    case 'inspectionReport': {
      const { data } = await inspectionReportsList({ ordering: ['inspection_date'] });
      return parseApiResponse(InspectionReportsListResponse, data, 'InspectionReport').results;
    }
    default:
      return [];
  }
};

const SupportingDataFields = ({ documentType }: { documentType?: UploadDocumentType }) => {
  const { control } = useFormContext<SupportingDataDto>();

  const forProp = getForProp(documentType);

  const { data, isLoading } = useSWR(
    forProp ? (['ticketOrInvoiceOptionsFetcher', forProp] as const) : null,
    ticketOrInvoiceCertificateOptionsFetcher,
    {},
  );
  const invoiceKind = useWatch({ control, name: 'invoiceKind' });

  let fieldName: string;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (forProp) {
    case 'invoice':
      fieldName = 'invoiceId';
      break;
    case 'certificate':
      fieldName = 'certificateId';
      break;
    case 'inspectionReport':
      fieldName = 'inspectionReportId';
      break;
    default:
      fieldName = 'ticketId';
  }

  const fields = useMemo(() => {
    let label;
    switch (fieldName) {
      case 'invoiceId':
        label = `Related to ${invoiceKind ?? 'Invoice or Proposal'} #`;
        break;
      case 'ticketId':
        label = 'Related to Ticket #';
        break;
      case 'certificateId':
        label = 'Related to Certificate';
        break;
      case 'inspectionReportId':
        label = 'Related to Inspection Report';
        break;
    }

    let options: { id: string; label: string }[] = [];

    if (data && !isLoading) {
      // Narrow down the data type based on the fieldName
      switch (fieldName) {
        case 'certificateId': {
          const docs = data as CertificateList[];
          options = docs.map((doc) => {
            const expirationDate = new Date(doc.expirationDate).toLocaleDateString();
            // `property` is null for a car with no property — the generated type
            // says so now, and these labels are the only readers.
            return {
              id: doc.id,
              label: `Certificate ${doc.property?.name ?? 'Unassigned'} - ${doc.car.name} (Expires: ${expirationDate})`,
            };
          });
          break;
        }
        case 'inspectionReportId': {
          const docs = data as InspectionReportList[];
          options = docs.map((doc) => {
            const inspectionDate = new Date(doc.inspectionDate).toLocaleDateString();
            return {
              id: doc.id,
              label: `Inspection Report ${doc.property?.name ?? 'Unassigned'} - ${doc.car.name} (Inspection Date: ${inspectionDate})`,
            };
          });
          break;
        }
        case 'invoiceId': {
          // data should be API_InvoiceFilterResponse['results']
          const docs = data as API_InvoiceFilterResponse['results'];
          options = docs.map((doc) => ({
            id: doc.id,
            label: doc.number,
          }));
          break;
        }
        case 'ticketId': {
          // data should be API_TicketFilterResponse['results']
          const docs = data as API_TicketFilterResponse['results'];
          options = docs.map((doc) => ({
            id: doc.id,
            label: doc.number,
          }));
          break;
        }
      }
    }

    return [
      {
        name: fieldName,
        label,
        type: 'autocomplete',
        options,
        loading: isLoading,
        required: true,
        gridProps: { xs: 12 },
      },
    ] as FieldDef[];
  }, [data, fieldName, invoiceKind, isLoading]);

  return (
    <>
      <Typography variant="h6" my={2}>
        General
      </Typography>
      <Grid container spacing={2} my={2}>
        {fields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
    </>
  );
};
